import { ReactElement } from 'react';
import { Controller, useController, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { DamWeekDayKey } from '~/providers/types/api-private';
import { FULL_DAY, PROGRAMMING_TITLE_MAX_LENGTH, SchedulePermission } from '../../schedules';
import { DayChips } from '../controls';
import { ModalShell } from './ModalShell';
import { PermissionField } from './PermissionField';
import { validateEndTime, validateStartTime } from './TimeInput24h';
import { TimeRangeFields } from './TimeRangeFields';
import {
  CancelLink, ModalBody, ModalDivider, ModalError, ModalLabel, ModalSubtitle, ModalTitle,
  SubmitButton, TextInput,
} from './styles';

export interface ScheduleModalValues {
  title: string;
  days: DamWeekDayKey[];
  start: string;
  end: string;
  fullDay: boolean;
  permission: SchedulePermission;
}

export const EMPTY_SCHEDULE_VALUES: ScheduleModalValues = {
  title: '', days: [], start: '', end: '', fullDay: false, permission: 'allow',
};

interface ScheduleModalProps {
  initialValues: ScheduleModalValues;
  isEdit: boolean;
  onClose: () => void;
  onSubmit: (values: ScheduleModalValues) => void;
}

export function ScheduleModal({
  initialValues, isEdit, onClose, onSubmit,
}: Readonly<ScheduleModalProps>): ReactElement {
  const { t } = useTranslation();
  const {
    control, register, handleSubmit, watch, formState: { errors },
  } = useForm<ScheduleModalValues>({ defaultValues: initialValues });
  const fullDay = watch('fullDay');
  const { field: startField } = useController({
    control, name: 'start', rules: { validate: (value, values) => validateStartTime(value, values.fullDay, t) },
  });
  const { field: endField } = useController({
    control, name: 'end', rules: { validate: (value, values) => validateEndTime(value, values.start, values.fullDay, t) },
  });

  function submit(values: ScheduleModalValues): void {
    onSubmit(values.fullDay ? { ...values, start: FULL_DAY.start, end: FULL_DAY.end } : values);
  }

  return (
    <ModalShell width={560} onClose={onClose}>
      <ModalBody onSubmit={handleSubmit(submit)} noValidate data-testid="eco-simplificado-modal-programacao">
        <ModalTitle>{t(isEdit ? 'ecoSimplificadoEditarProgramacao' : 'ecoSimplificadoAdicionarProgramacaoTitulo')}</ModalTitle>
        <ModalSubtitle>{t('ecoSimplificadoProgramacaoSubtitulo')}</ModalSubtitle>

        <ModalLabel htmlFor="eco-simplificado-titulo">{t('ecoSimplificadoTitulo')}</ModalLabel>
        <TextInput
          id="eco-simplificado-titulo"
          placeholder={t('ecoSimplificadoDigiteTitulo')}
          {...register('title', {
            validate: (value) => value.trim().length <= PROGRAMMING_TITLE_MAX_LENGTH
              || t('ecoSimplificadoTituloMuitoLongo', { max: PROGRAMMING_TITLE_MAX_LENGTH }),
          })}
        />
        {errors.title ? <ModalError>{errors.title.message}</ModalError> : null}

        <ModalDivider />

        <ModalLabel as="div" style={{ marginTop: 0, marginBottom: 10 }}>{t('selecioneDias')}</ModalLabel>
        <Controller
          control={control}
          name="days"
          rules={{ validate: (value) => value.length > 0 || t('selecioneAoMenosUmDia') }}
          render={({ field }) => (
            <DayChips
              selected={field.value}
              onToggle={(day) => field.onChange(field.value.includes(day) ? field.value.filter((item) => item !== day) : [...field.value, day])}
            />
          )}
        />
        {errors.days ? <ModalError>{errors.days.message}</ModalError> : null}

        <TimeRangeFields
          start={{
            id: 'eco-simplificado-inicio', label: t('ecoSimplificadoHorarioInicio'), field: startField, error: errors.start?.message,
          }}
          end={{
            id: 'eco-simplificado-fim', label: t('ecoSimplificadoHorarioTermino'), field: endField, error: errors.end?.message,
          }}
          fullDay={fullDay}
          fullDayLabel={t('ecoSimplificadoDiaInteiro')}
          fullDayInput={register('fullDay')}
        />

        <Controller
          control={control}
          name="permission"
          render={({ field }) => <PermissionField value={field.value} onChange={field.onChange} testId="eco-simplificado-funcionamento" />}
        />

        <SubmitButton type="submit">{t(isEdit ? 'ecoSimplificadoSalvar' : 'botaoAdicionar')}</SubmitButton>
        <CancelLink type="button" onClick={onClose}>{t('cancelar')}</CancelLink>
      </ModalBody>
    </ModalShell>
  );
}
